import { useMemo, useRef } from 'react'

import {
  CustomFieldRenderProps,
  F0Form,
  F0FormRef,
  f0FormField,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  EntitySelect,
  EntitySelectEntity,
  NumberInput,
  RichTextEditor,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsGetContractTypesDocument,
  AtsGetJobPostingSettingsDescriptionDocument,
  AtsJobPostingSettingsDescriptionFragment,
  AtsUpdateJobPostingDescriptionDocument,
  ContractTypeFragment,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { z } from 'zod'

import TextEditor from 'modules/core/components/TextEditor'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobCatalogLinkedInfoAlert } from 'modules/ats/pages/JobPostingSettings/JobCatalogAlerts/LinkedInfoAlert'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { DescriptionSkeleton } from './DescriptionSkeleton'
import { GenerateDraftDescription, useGenerateDraftDescription } from './GenerateDraftDescription'
import { GenerateDraftLoading } from './GenerateDraftDescription/GenerateDraftLoading'

interface Props {
  jobId: string
}

export const Description = ({ jobId }: Props) => {
  const { company } = useCurrentSession()
  const { canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const { data, loading } = useQuery(AtsGetJobPostingSettingsDescriptionDocument, {
    variables: {
      id: jobId,
      companyId: company.id,
    },
  })

  const { data: contractTypesData, loading: loadingContractTypes } = useQuery(
    AtsGetContractTypesDocument
  )

  const isLoading = loading || loadingContractTypes || richTextEditorLoading

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessDescription)
  }, [])

  const job = data?.ats.jobPosting
  const enabledFeatures = data?.enabledFeatures.featuresConnection.nodes ?? []
  const currencies = data?.apiCore?.currenciesConnection?.edges ?? []

  const jobCatalogFeatureEnabled =
    enabledFeatures.find((feature) => feature.name === 'dev_ats_job_posting_revamp')?.enabled ??
    false

  if (isLoading || !job) {
    return <DescriptionSkeleton />
  }

  return (
    <DescriptionForm
      job={job}
      contractTypes={contractTypesData?.ats.contractTypesConnection.nodes || []}
      currencies={currencies}
      canUseRichTextEditor={canUseRichTextEditor}
      jobCatalogSerializedTree={data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? ''}
      jobCatalogFeatureEnabled={jobCatalogFeatureEnabled}
    />
  )
}

interface DescriptionFormProps {
  job: AtsJobPostingSettingsDescriptionFragment
  contractTypes: ContractTypeFragment[]
  currencies: ReadonlyArray<{ node: { isoCode: string; priority: number | null } }>
  canUseRichTextEditor: boolean
  jobCatalogSerializedTree: string
  jobCatalogFeatureEnabled: boolean
}

const SALARY_PERIODS = ['annual', 'monthly', 'daily'] as const
const SALARY_FORMATS = ['range', 'fixed_amount'] as const
const SCHEDULE_TYPES = ['full_time', 'part_time'] as const

const convertToCents = (value: number | undefined | null) =>
  !!value && isFinite(value) ? Math.round(value * 100) : 0

const convertFromCents = (value: number | undefined | null) => (value ? value / 100 : 0)

const DescriptionForm = ({
  job,
  canUseRichTextEditor,
  contractTypes,
  currencies,
  jobCatalogSerializedTree,
  jobCatalogFeatureEnabled,
}: DescriptionFormProps) => {
  const { has_ats_ai_job_posting_description } = useFeatures(['ats_ai_job_posting_description'])
  const { editorConfig } = useRichTextEditor()
  const formRef = useRef<F0FormRef | null>(null)

  const [updateJobPosting] = useMutation(AtsUpdateJobPostingDescriptionDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const {
    generateDraftDescription,
    generatingDraftDescription,
    alreadyGeneratedDraftDescription,
    draftDescription,
  } = useGenerateDraftDescription(job.id)

  const salaryPeriodOptions = useMemo(
    () => [
      { label: i18n.t('ats.job_salary_period.annual'), value: 'annual' as const },
      { label: i18n.t('ats.job_salary_period.monthly'), value: 'monthly' as const },
      { label: i18n.t('ats.job_salary_period.daily'), value: 'daily' as const },
    ],
    []
  )

  const salaryFormatOptions = useMemo(
    () => [
      { label: i18n.t('ats.job_salary_format.range'), value: 'range' as const },
      { label: i18n.t('ats.job_salary_format.fixed_amount'), value: 'fixed_amount' as const },
    ],
    []
  )

  const scheduleTypeOptions = useMemo(
    () => [
      { label: i18n.t('ats.job_schedule.full_time'), value: 'full_time' as const },
      { label: i18n.t('ats.job_schedule.part_time'), value: 'part_time' as const },
    ],
    []
  )

  const currencyOptions = useMemo(
    () =>
      [...currencies]
        .map((c) => ({ label: c.node.isoCode, value: c.node.isoCode }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [currencies]
  )

  const formattedContractTypes = useMemo(
    () =>
      contractTypes.map(({ id, label, country }) => ({
        label,
        country,
        value: id,
      })),
    [contractTypes]
  )

  const contractTypesEntities: EntitySelectEntity[] = useMemo(() => {
    const groupedFormattedContractTypes = groupBy(
      formattedContractTypes,
      (item) => item.country ?? 'all'
    )
    const entities: EntitySelectEntity[] = Object.entries(groupedFormattedContractTypes).map(
      ([countryKey, items]) => ({
        id: `group_${countryKey}`,
        name: countryKey,
        expanded: true,
        subItems: items
          .filter((i) => i.label)
          .sort((x, y) => x.label.localeCompare(y.label))
          .map(({ value, label, country: countryCode }) => ({
            subId: value,
            subName: label,
            subSearchKeys: [label, value, countryCode ?? ''],
          })),
      })
    )
    return entities
  }, [formattedContractTypes])

  const showLinkedInformationAlert =
    jobCatalogFeatureEnabled && !!(job.jobCatalogNodeRolesUuid && job.jobCatalogNodeLevelsUuid)

  const schema = useMemo(
    () =>
      z.object({
        contractType: f0FormField(z.string().nullable(), {
          label: i18n.t('ats.job_posting_settings.details.contract_type_label'),
          section: 'details',
          row: 'contract-schedule',
          fieldType: 'custom',
          render: (props: CustomFieldRenderProps<string | null>) => {
            const selectedEntities: EntitySelectEntity[] = (() => {
              const selectedId = props.value
              if (!selectedId) return []
              for (const group of contractTypesEntities) {
                const sub = group.subItems?.find((s) => s.subId === selectedId)
                if (sub) return [{ ...group, subItems: [sub] }]
              }
              return []
            })()

            return (
              <EntitySelect
                size="md"
                label={i18n.t('ats.job_posting_settings.details.contract_type_label')}
                notFoundTitle={i18n.t(
                  'ats.job_posting_settings.description.contract_type.not_found'
                )}
                notFoundSubtitle={''}
                placeholder={i18n.t('common.select_basic')}
                onSelect={(entity) => {
                  if (!entity?.subItems?.[0]) {
                    return
                  }
                  const id = entity.subItems[0].subId
                  if (typeof id === 'string') {
                    props.onChange(id)
                  }
                }}
                entities={contractTypesEntities}
                groups={[]}
                selectedGroup="all"
                onItemExpandedChange={(_id, _expanded) => {}}
                onGroupChange={(_value) => {}}
                selectedEntities={selectedEntities}
                selectedItemsCopy={i18n.t(
                  'job_catalog.job_catalog_tree_selector.role.trigger_selected'
                )}
                hiddenAvatar
                singleSelector
              />
            )
          },
        }),
        scheduleType: f0FormField(z.enum(SCHEDULE_TYPES), {
          label: i18n.t('ats.job_posting_settings.details.schedule_type_label'),
          placeholder: i18n.t('common.select_basic'),
          section: 'details',
          row: 'contract-schedule',
          fieldType: 'select',
          options: scheduleTypeOptions,
        }),
        salaryPeriod: f0FormField(z.enum(SALARY_PERIODS), {
          label: i18n.t('ats.job_posting_settings.salary.salary_period_label'),
          placeholder: i18n.t('common.select_basic'),
          section: 'salary',
          row: 'salary-period-format',
          fieldType: 'select',
          options: salaryPeriodOptions,
          renderIf: () => job.salaryPermission ?? false,
        }),
        salaryFormat: f0FormField(z.enum(SALARY_FORMATS), {
          label: i18n.t('ats.job_posting_settings.salary.salary_format_label'),
          placeholder: i18n.t('common.select_basic'),
          section: 'salary',
          row: 'salary-period-format',
          fieldType: 'select',
          options: salaryFormatOptions,
          renderIf: () => job.salaryPermission ?? false,
        }),
        salaryCurrency: f0FormField(z.string().min(1), {
          label: i18n.t('ats.job_posting_settings.salary.currency_label'),
          placeholder: i18n.t('common.select'),
          section: 'salary',
          row: 'salary-amounts',
          fieldType: 'select',
          options: currencyOptions,
          showSearchBox: true,
          renderIf: () => job.salaryPermission ?? false,
        }),
        salaryFromAmountInCents: f0FormField(z.number(), {
          label: i18n.t('ats.job_posting_settings.salary.salary_from_amount'),
          section: 'salary',
          row: 'salary-amounts',
          fieldType: 'custom',
          renderIf: () => job.salaryPermission ?? false,
          render: function SalaryFromAmountRender(props: CustomFieldRenderProps<number>) {
            const values = formRef.current?.getValues() ?? {}
            const isRange = values['salaryFormat'] === 'range'
            const rawCurrency = values['salaryCurrency']
            const currency = typeof rawCurrency === 'string' ? rawCurrency : ''
            return (
              <NumberInput
                label={
                  isRange
                    ? i18n.t('ats.job_posting_settings.salary.salary_from_amount_range')
                    : i18n.t('ats.job_posting_settings.salary.salary_from_amount')
                }
                locale={i18n.locale}
                placeholder={i18n.t('common.select_basic')}
                value={props.value}
                onChange={(value) => props.onChange(Number(value))}
                units={currency}
                size="md"
              />
            )
          },
        }),
        salaryToAmountInCents: f0FormField(z.number(), {
          label: i18n.t('ats.job_posting_settings.salary.salary_to_amount'),
          section: 'salary',
          row: 'salary-amounts',
          fieldType: 'custom',
          renderIf: ({ values }) => {
            if (!(job.salaryPermission ?? false)) return false
            const salaryFormat = values['salaryFormat']
            return salaryFormat === 'range'
          },
          render: function SalaryToAmountRender(props: CustomFieldRenderProps<number>) {
            const rawCurrency = formRef.current?.getValues()['salaryCurrency']
            const currency = typeof rawCurrency === 'string' ? rawCurrency : ''
            return (
              <NumberInput
                label={props.label}
                locale={i18n.locale}
                value={props.value}
                onChange={(value) => props.onChange(Number(value))}
                units={currency}
                error={props.error}
                size="md"
              />
            )
          },
        }),
        hideSalary: f0FormField(z.boolean(), {
          label: i18n.t('ats.job_posting_settings.salary.hide_salary_label'),
          helpText: i18n.t('ats.job_posting_settings.salary.hide_salary_tooltip'),
          section: 'salary',
          fieldType: 'checkbox',
          renderIf: () => job.salaryPermission ?? false,
        }),
        descriptionDraft: f0FormField(z.string().optional(), {
          label: '',
          section: 'description',
          fieldType: 'custom',
          renderIf: () => has_ats_ai_job_posting_description,
          render: function GenerateDraftDescriptionRender() {
            const rawDescription = formRef.current?.getValues()['description']
            const currentDescription = typeof rawDescription === 'string' ? rawDescription : ''
            return (
              <GenerateDraftDescription
                onClickGenerate={generateDraftDescription}
                loading={generatingDraftDescription}
                alreadyGenerated={alreadyGeneratedDraftDescription}
                currentDescription={currentDescription}
              />
            )
          },
        }),
        description: f0FormField(z.string(), {
          label: i18n.t('ats.job_posting_settings.description.title'),
          section: 'description',
          fieldType: 'custom',
          render: (props: CustomFieldRenderProps<string>) => {
            if (generatingDraftDescription) {
              return <GenerateDraftLoading />
            }
            if (canUseRichTextEditor) {
              return (
                <RichTextEditor
                  {...editorConfig}
                  title={i18n.t('ats.job_posting_settings.description.title')}
                  onChange={(data) => props.onChange(data?.value ?? '')}
                  placeholder={i18n.t('ats.job_posting_settings.description.placeholder')}
                  initialEditorState={{
                    content: draftDescription ?? job.description ?? '',
                  }}
                  maxCharacters={10000}
                />
              )
            }
            return (
              <TextEditor
                onChange={(description) => {
                  if (typeof description === 'string') {
                    props.onChange(description)
                  } else if (description === null) {
                    props.onChange('')
                  }
                }}
                placeholder={i18n.t('ats.job_posting_settings.description.placeholder')}
                initialEditorState={{
                  data: draftDescription ?? props.value,
                }}
                height="s600"
                enableBetaFeatures={false}
              />
            )
          },
        }),
      }),
    [
      contractTypesEntities,
      scheduleTypeOptions,
      salaryPeriodOptions,
      salaryFormatOptions,
      currencyOptions,
      job.salaryPermission,
      job.description,
      canUseRichTextEditor,
      editorConfig,
      generatingDraftDescription,
      alreadyGeneratedDraftDescription,
      generateDraftDescription,
      has_ats_ai_job_posting_description,
      draftDescription,
    ]
  )

  const formDefinition = useF0FormDefinition({
    name: 'ats-job-posting-description',
    schema,
    sections: {
      details: {
        title: i18n.t('ats.job_posting_settings.details.title'),
      },
      salary: {
        title: i18n.t('ats.job_posting_settings.salary.title'),
      },
      description: {
        title: i18n.t('ats.job_posting_settings.description.title'),
      },
    },
    defaultValues: {
      contractType: job.contractType ?? null,
      scheduleType: job.scheduleType ?? undefined,
      salaryPeriod: job.salaryPeriod ?? 'annual',
      salaryFormat: job.salaryFormat ?? undefined,
      salaryCurrency: job.salaryCurrency ?? job.legalEntity?.currency ?? '',
      salaryFromAmountInCents: convertFromCents(job.salaryFromAmountInCents),
      salaryToAmountInCents: convertFromCents(job.salaryToAmountInCents),
      hideSalary: job.hideSalary ?? false,
      description: job.description ?? '',
    },
    submitConfig: {
      type: 'default',
      label: i18n.t('common.submit'),
    },
    errorTriggerMode: 'on-submit',
    onSubmit: async ({ data }) => {
      track(trackingEvents.ats.jobSettingsDescriptionSubmit)

      let fieldErrors: Record<string, string> = {}

      const matchedContractType = data.contractType
        ? (contractTypes.find((ct) => ct.id === data.contractType)?.id ?? null)
        : null

      const result = await updateJobPosting({
        variables: {
          id: job.id,
          contractType: matchedContractType,
          description: data.description,
          hideSalary: data.hideSalary,
          salaryCurrency: data.salaryCurrency,
          salaryFormat: data.salaryFormat || null,
          salaryFromAmountInCents: convertToCents(data.salaryFromAmountInCents),
          salaryPeriod: data.salaryPeriod,
          salaryToAmountInCents: convertToCents(data.salaryToAmountInCents),
          scheduleType: data.scheduleType || null,
        },
      })

      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        return { success: true }
      }

      return { success: false, errors: fieldErrors }
    },
  })

  return (
    <SectionComponentLayout
      sidebar={
        showLinkedInformationAlert && (
          <JobCatalogLinkedInfoAlert
            jobCatalogSerializedTree={jobCatalogSerializedTree}
            jobCatalogLevelUuid={job.jobCatalogNodeLevelsUuid ?? ''}
            jobCatalogRoleUuid={job.jobCatalogNodeRolesUuid ?? ''}
          />
        )
      }
    >
      <F0Form formDefinition={formDefinition} formRef={formRef} />
    </SectionComponentLayout>
  )
}
